// Browser-compatible Neynar service using REST API
const NEYNAR_API_KEY = import.meta.env.VITE_NEYNAR_API_KEY;
const NEYNAR_BASE_URL = 'https://api.neynar.com/v2';

interface NeynarUser {
  fid: number;
  username: string;
  display_name: string;
  pfp_url: string;
  profile: {
    bio: {
      text: string;
    };
  };
  follower_count: number;
  following_count: number;
}

interface NeynarSigner {
  signer_uuid: string;
  public_key: string;
  status: 'pending_approval' | 'approved' | 'revoked';
  signer_approval_url?: string;
}

interface CreateSignerResponse {
  signer_uuid: string;
  public_key: string;
  status: 'pending_approval';
  signer_approval_url: string;
}

interface PublishCastResponse {
  cast: {
    hash: string;
    author: {
      fid: number;
      username: string;
    };
    text: string;
    timestamp: string;
  };
}

// Demo mode fallback data
const DEMO_USER = {
  fid: 12345,
  username: 'demo_user',
  display_name: 'Demo User',
  pfp_url: 'https://via.placeholder.com/150',
  profile: {
    bio: {
      text: 'This is a demo user for testing purposes'
    }
  },
  follower_count: 100,
  following_count: 50
};

const DEMO_SIGNER = {
  signer_uuid: 'demo-signer-uuid',
  public_key: 'demo-public-key',
  status: 'approved' as const,
};

class NeynarService {
  private apiKey: string | null;
  private isDemoMode: boolean;
  private hasPaymentIssue: boolean = false;

  constructor() {
    this.apiKey = NEYNAR_API_KEY || null;
    this.isDemoMode = !this.apiKey;

    if (this.isDemoMode && import.meta.env.DEV) {
      console.log('🎭 Running in demo mode - no real Farcaster integration');
    }
  }

  private async makeRequest(endpoint: string, options: RequestInit = {}): Promise<any> {
    if (this.isDemoMode || this.hasPaymentIssue) {
      throw new Error('Demo mode: API calls not available');
    }

    const url = `${NEYNAR_BASE_URL}${endpoint}`;
    const response = await fetch(url, {
      ...options,
      headers: {
        'Authorization': `Bearer ${this.apiKey}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.text();
      
      // Handle specific error codes
      if (response.status === 402) {
        this.hasPaymentIssue = true;
        throw new Error('Neynar API requires a paid subscription. Please upgrade your plan at https://neynar.com/pricing');
      }
      
      if (response.status === 401) {
        throw new Error('Invalid API key. Please check your Neynar API key.');
      }
      
      if (response.status === 429) {
        throw new Error('Rate limit exceeded. Please try again later.');
      }
      
      throw new Error(`Neynar API error: ${response.status} - ${error}`);
    }

    return response.json();
  }

  async createSigner(): Promise<CreateSignerResponse> {
    if (this.isDemoMode) {
      // Simulate async operation
      await new Promise(resolve => setTimeout(resolve, 500));
      return {
        signer_uuid: DEMO_SIGNER.signer_uuid,
        public_key: DEMO_SIGNER.public_key,
        status: 'pending_approval',
        signer_approval_url: 'https://farcaster.xyz/~/add-cast-action'
      };
    }

    return this.makeRequest('/farcaster/signer', {
      method: 'POST',
    });
  }

  async lookupSigner(signerUuid: string): Promise<NeynarSigner> {
    if (this.isDemoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return DEMO_SIGNER;
    }

    const response = await this.makeRequest(`/farcaster/signer?signer_uuid=${signerUuid}`);
    return response.signer;
  }

  async fetchUser(fid: number): Promise<NeynarUser> {
    if (this.isDemoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return DEMO_USER;
    }

    const response = await this.makeRequest(`/farcaster/user/bulk?fids=${fid}`);
    return response.users[0];
  }

  async publishCast(params: {
    signerUuid: string;
    text: string;
    embeds?: Array<{ url: string }>;
    replyTo?: string;
    channelId?: string;
  }): Promise<PublishCastResponse> {
    if (this.isDemoMode) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      return {
        cast: {
          hash: '0x' + Math.random().toString(16).substring(2, 10),
          author: {
            fid: DEMO_USER.fid,
            username: DEMO_USER.username,
          },
          text: params.text,
          timestamp: new Date().toISOString(),
        }
      };
    }

    return this.makeRequest('/farcaster/cast', {
      method: 'POST',
      body: JSON.stringify({
        signer_uuid: params.signerUuid,
        text: params.text,
        embeds: params.embeds,
        parent: params.replyTo,
        channel_id: params.channelId,
      }),
    });
  }

  async validateFrameAction(frameActionPayload: any): Promise<any> {
    if (this.isDemoMode) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return {
        valid: true,
        action: {
          interactor: DEMO_USER,
        }
      };
    }

    return this.makeRequest('/farcaster/frame/validate', {
      method: 'POST',
      body: JSON.stringify(frameActionPayload),
    });
  }

  getDemoMode(): boolean {
    return this.isDemoMode || this.hasPaymentIssue;
  }
}

export const neynarService = new NeynarService();
export type { NeynarUser, NeynarSigner, CreateSignerResponse, PublishCastResponse }; 
